import { EventType, type AuthenticationResult, type AuthError, PublicClientApplication } from '@azure/msal-browser';
import { env } from './env';

export const scopes = [env.VITE_APP_ACCESS_SCOPE];

export const msalConfig = {
  auth: {
    clientId: env.VITE_APP_CLIENT_ID,
    authority: env.VITE_APP_SIGN_IN_AUTHORITY,
    knownAuthorities: [env.VITE_APP_AUTHORITY_DOMAIN],
    redirectUri: env.VITE_APP_REDIRECT_URI,
    postLogoutRedirectUri: '/',
    navigateToLoginRequestUrl: true
  },
  cache: {
    cacheLocation: env.VITE_APP_TOKEN_CACHE_LOCATION || 'localStorage',
    storeAuthStateInCookie: false
  }
};

export const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.addEventCallback((event) => {
  if (event.eventType !== EventType.LOGIN_SUCCESS || !event.payload) {
    return;
  }

  const authResult = event.payload as AuthenticationResult;
  msalInstance.setActiveAccount(authResult.account);
});

function isRecoverableRedirectHistoryError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const authError = error as Partial<AuthError>;
  const combined = `${authError.name ?? ''} ${authError.message ?? ''} ${authError.errorCode ?? ''}`.toLowerCase();

  return (
    combined.includes('state_mismatch') ||
    combined.includes('hash_empty_error') ||
    combined.includes('hash_does_not_contain_known_properties')
  );
}

function replaceWithCleanAppUrl(): void {
  const cleanUrl = new URL(globalThis.location.origin);
  cleanUrl.pathname = '/';
  cleanUrl.search = '';
  cleanUrl.hash = '';

  globalThis.location.replace(cleanUrl.toString());
}

let msalInitializationPromise: Promise<void> | null = null;

export const initializeMsal = (): Promise<void> => {
  msalInitializationPromise ??= (async () => {
    await msalInstance.initialize();

    try {
      const redirectResult = await msalInstance.handleRedirectPromise();

      if (redirectResult?.account) {
        msalInstance.setActiveAccount(redirectResult.account);
        return;
      }
    } catch (error) {
      if (isRecoverableRedirectHistoryError(error)) {
        replaceWithCleanAppUrl();
        return;
      }

      throw error;
    }

    const cachedAccounts = msalInstance.getAllAccounts();
    if (!msalInstance.getActiveAccount() && cachedAccounts.length === 1) {
      msalInstance.setActiveAccount(cachedAccounts[0]);
    }
  })();

  return msalInitializationPromise;
};
